import { Component, computed, inject, signal, TemplateRef, ViewChild } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { dateTimeWithoutTimezone, formatAsUTC, monthOnlyFormat } from 'src/app/shared/utilities/date'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { DataTableComponent } from '../table/data-table.component'
import { ColDef, GetRowIdFunc } from 'ag-grid-community'
import { catchError, combineLatest, from, NEVER, switchMap, tap } from 'rxjs'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { withHeader } from '../table/headers/table-header.component'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { EntityFilterModalComponent } from '../modals/filters/entity-filter-modal.component'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { ToastService } from '../toasts/toasts.service'
import { MatMenuModule } from '@angular/material/menu'
import { TBillSettlementsService } from '../settlements/tbill-settlements.service'
import { PaginationParams } from 'src/app/shared/utilities/http-params'
import { TBillNITSRate, TBillNITSRateCreateRequest, TBillNITSRateUpdateRequest } from '../settlements/tbill-settlements.types'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { TableSortService } from '../table/services/table-sort.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { StringFilter } from 'src/app/shared/services/filter/string-filter'
import openModal from 'src/app/shared/services/openModal'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { CreateTBillNITSRateModalComponent } from './create-tbill-nits-rate-modal.component'
import { UpdateTBillNITSRateModalComponent } from './update-tbill-nits-rate-modal.component'
import { pipe } from 'lodash/fp'
import { NumberRangeFilter } from 'src/app/shared/services/filter/number-range-filter'
import { actionColumn, withCell } from '../table/cells/with-cell'
import { DateCellComponent } from '../table/cells/date-cell.component'
import { NumberCellComponent } from '../table/cells/number-cell.component'
import { NumberRangeFilterModalComponent } from '../modals/filters/number-range-filter-modal.component'
import { EntityCellComponent } from '../table/cells/entity-cell.component'
import { DateDisplayComponent } from '../date-display/date-display.component'
import { NumberDisplayComponent } from '../number-display/number-display-component'

@Component({
  selector: 'app-tbill-schedule-9',
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    LabeledPropertyComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    TablePaginationComponent,
    DateDisplayComponent,
    NumberDisplayComponent,
  ],
  providers: [TableSortService],
  styles: '',
  template: `
    <div app-page-header header="Schedule 9 Rates (NITS)" [menuItems]="pageHeaderMenuItems"></div>
    <div app-page-content>
        <app-active-filter-list [filterGroup]="constructor.name"></app-active-filter-list>
        <app-data-table [loading]="loading()" [data]="data()" [getRowId]="getRowId" [columnDefs]="columnDefs"></app-data-table>
        <app-table-pagination
            [pageNumber]="pageNumber()"
            [pageSize]="pageSize()"
            [totalResults]="totalResults()"
            (pageNumberChange)="pageNumber.set($event)"
            (pageSizeChange)="pageSize.set($event)"
        />
    </div>

    <ng-template #deleteConfirmation let-ctx="ctx">
        Are you sure you want to delete Schedule 9 Rate?
        @if(ctx) {
            <div app-labeled-property label="Control Area">{{ctx.control_area_code}}</div>
            <div app-labeled-property label="Month">
              <div app-date-display [value]="ctx.month" [format]="monthOnlyFormat"></div>
            </div>
            <div app-labeled-property label="Rate ($/(MW\u00A0MTH))">
              <div app-number-display [value]="ctx.rate"></div>
            </div>
        }
    </ng-template>
    `,
})
export class TBillNITSRatesComponent {
  protected readonly monthOnlyFormat = monthOnlyFormat
  protected readonly dateTimeWithoutTimezone = dateTimeWithoutTimezone

  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)
  private readonly toastService = inject(ToastService)
  private readonly filterService = inject(FilterService)
  private readonly sortService = inject(TableSortService)
  private readonly modalService = inject(NgbModal)
  private readonly tbillSettlementsService = inject(TBillSettlementsService)

  @ViewChild('deleteConfirmation')
  deleteConfirmationTemplate: TemplateRef<{ ctx: TBillNITSRate }>

  protected readonly refresh = signal(0)
  protected readonly loading = signal(false)
  protected readonly pageSize = signal(25)
  protected readonly pageNumber = signal(1)
  protected readonly totalResults = signal(0)
  protected readonly data = signal<TBillNITSRate[]>([])
  protected readonly getRowId: GetRowIdFunc<TBillNITSRate> = (data) => data.data.control_area_code + formatAsUTC(data.data.month)
  protected readonly paginationParams = computed<PaginationParams>(() => ({
    pageNumber: this.pageNumber(),
    pageSize: this.pageSize(),
  }))

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Create', onClick: () => this.openCreateModal() },
  ]

  protected readonly columnDefs: ColDef[] = [
    pipe(
      withHeader<TBillNITSRate, EntityFilterModalComponent>({
        headerName: 'Control Area',
        field: 'control_area_code',
        filterable: true,
        sortable: true,
        filterGroup: TBillNITSRatesComponent.name,
        modal: EntityFilterModalComponent,
        modalInit: {},
      }),
      withCell<EntityCellComponent<TBillNITSRate, 'control_area_code'>>(
        EntityCellComponent,
        {
          value: (d) => d.control_area_code,
        },
      ))({
      flex: 1,
      editable: false,
      minWidth: 150,
      width: 150,
    }),
    pipe(
      withHeader<TBillNITSRate, DateRangeFilterModalComponent>({
        headerName: 'Month',
        field: 'month',
        filterable: true,
        sortable: true,
        filterGroup: TBillNITSRatesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillNITSRate>>(
        DateCellComponent,
        {
          date: (d) => d.month,
          format: monthOnlyFormat,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillNITSRate, NumberRangeFilterModalComponent>({
        headerName: 'Rate ($/(MW\u00A0MTH))',
        field: 'rate',
        filterable: true,
        sortable: true,
        filterGroup: TBillNITSRatesComponent.name,
        modal: NumberRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<NumberCellComponent<TBillNITSRate>>(
        NumberCellComponent,
        {
          num: (d) => d.rate,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 190,
    }),
    pipe(
      withHeader<TBillNITSRate, DateRangeFilterModalComponent>({
        headerName: 'Created On (STL)',
        field: 'create_time',
        filterable: true,
        sortable: true,
        filterGroup: TBillNITSRatesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillNITSRate>>(
        DateCellComponent,
        {
          date: (d) => d.create_time,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillNITSRate, StringFilterModalComponent>({
        headerName: 'Created By',
        field: 'created_by',
        filterable: true,
        sortable: true,
        filterGroup: TBillNITSRatesComponent.name,
        modal: StringFilterModalComponent,
        modalInit: {},
      }),
    )({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    pipe(
      withHeader<TBillNITSRate, DateRangeFilterModalComponent>({
        headerName: 'Last Updated (STL)',
        field: 'last_update_time',
        filterable: true,
        sortable: true,
        filterGroup: TBillNITSRatesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillNITSRate>>(
        DateCellComponent,
        {
          date: (d) => d.last_update_time,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillNITSRate, StringFilterModalComponent>({
        headerName: 'Last Updated By',
        field: 'last_updated_by',
        filterable: true,
        sortable: true,
        filterGroup: TBillNITSRatesComponent.name,
        modal: StringFilterModalComponent,
        modalInit: {},
      }))({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    actionColumn<TBillNITSRate>(TBillNITSRatesComponent.name, {
      actions: [
        {
          name: 'Update',
          onAction: (d: TBillNITSRate) => this.openUpdateModal(d),
        },
        {
          name: 'Delete',
          onAction: (d: TBillNITSRate) => this.openDeleteModal(d),
        },
      ],
    }),
  ]

  constructor() {
    const { dispatch } = this.filterService
    dispatch(FilterService.InitFiltersAction({
      fieldGroup: TBillNITSRatesComponent.name,
      fields: {
        month: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: true,
            onlyMonth: true,
            meta: {
              fieldGroup: TBillNITSRatesComponent.name,
              fieldName: 'month',
              fieldDisplay: 'Month',
            },
          },
        }, dispatch),
        control_area_code: new EntityFilter({
          config: {
            resource: 'controlAreas',
            allowMultiple: true,
            allowWildcards: false,
            meta: {
              fieldGroup: TBillNITSRatesComponent.name,
              fieldName: 'control_area_code',
              fieldDisplay: 'Control Area',
            },
          },
        }, dispatch),
        rate: new NumberRangeFilter({
          config: {
            allowRange: true,
            precision: 8,
            meta: {
              fieldGroup: TBillNITSRatesComponent.name,
              fieldName: 'rate',
              fieldDisplay: '($/(MW\u00A0MTH))',
            },
          },
        }, dispatch),
        create_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: {
              fieldGroup: TBillNITSRatesComponent.name,
              fieldName: 'create_time',
              fieldDisplay: 'Created On (STL)',
            },
          },
        }, dispatch),
        created_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: TBillNITSRatesComponent.name,
              fieldName: 'created_by',
              fieldDisplay: 'Created By',
            },
          },
        }, dispatch),
        last_update_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: {
              fieldGroup: TBillNITSRatesComponent.name,
              fieldName: 'last_update_time',
              fieldDisplay: 'Last Updated (STL)',
            },
          },
        }, dispatch),
        last_updated_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: TBillNITSRatesComponent.name,
              fieldName: 'last_updated_by',
              fieldDisplay: 'Last Updated By',
            },
          },
        }, dispatch),
      },
    }))

    combineLatest([
      toObservable(this.paginationParams),
      this.filterService
        .asParams(TBillNITSRatesComponent.name)
        .pipe(
          tap(() => {
            if (this.pageNumber() !== 1) {
              this.pageNumber.set(1)
            }
          }),
        ),
      this.sortService.getOrder(),
      toObservable(this.refresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([pagination, filters, order]) => this.tbillSettlementsService.getTBillNITSRates(pagination, filters, order)),
        catchError(this.handleLoadingError('Something went wrong loading tbill retailer usage overrides!')),
      )
      .subscribe({
        error: this.handleLoadingError('Something went wrong loading tbill retailer usage overrides!'),
        next: (res) => {
          this.totalResults.set(res.total)
          this.data.set(res.results)
          this.loading.set(false)
        },
      })

    from(this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      TBillNITSRatesComponent.name,
    ))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  protected handleLoadingError(defaultMessage: string){
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e: any) => {
      this.data.set([])
      this.loading.set(false)
      this.toastService.show({ type: 'error', message: e.detail ?? defaultMessage })
      return NEVER
    }
  }

  async openCreateModal() {
    openModal(
      this.modalService,
      CreateTBillNITSRateModalComponent,
      { centered: true },
    ).init({
      modalState: {},
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onCreate: this.onCreate.bind(this),
    })
  }

  async onCreate(req: TBillNITSRateCreateRequest) {
    this.tbillSettlementsService.createTBillNITSRate(req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Creation successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  async openUpdateModal(data: TBillNITSRate) {
    openModal(
      this.modalService,
      UpdateTBillNITSRateModalComponent,
      { centered: true },
    ).init({
      data,
      modalState: {
        rate: data.rate,
      },
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onUpdate: this.onUpdate.bind(this),
    })
  }

  async onUpdate(control_area_uid: string, market_interval: Date, req: TBillNITSRateUpdateRequest) {
    this.tbillSettlementsService.updateTBillNITSRate(control_area_uid, market_interval, req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong updating this override!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Update successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  async openDeleteModal(data: TBillNITSRate) {
    openModal(
      this.modalService,
      ConfirmationModalComponent,
      { centered: true },
    ).init({
      headerText: 'Confirm Deletion',
      confirmButtonText: 'Delete',
      bodyTemplate: this.deleteConfirmationTemplate,
      bodyContext: data,
      onCancel: () => {
        this.modalService.dismissAll()
      },
      onConfirm: () => {
        this.onDelete(data.control_area_code, data.month)
      },
    })
  }

  async onDelete(control_area_code: string, month: Date) {
    this.tbillSettlementsService.deleteTBillNITSRate(control_area_code, month)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong deleting this override!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Deletion successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }
}